/**
 * useFoundationAutoStart — Round 218A Phase 8D-3D (2026-02-07).
 *
 * When a downstream Money Bot builder opens AND the customer's
 * Foundation is complete AND their Business Memory has already
 * populated the required inputs, we auto-invoke the builder's
 * generate function ONCE so the customer never sees a redundant
 * "Who is your target customer?" form for information they've
 * already given us.
 *
 * The customer keeps an escape hatch: if they explicitly click
 * "Edit inputs" (or the auto-start fails), the normal input step
 * still renders — this hook does NOT block the input step, it only
 * short-circuits it when everything the builder needs is already
 * on file.
 *
 * Usage inside a builder:
 *
 *   useFoundationAutoStart({
 *     isOpen,
 *     ready: !!businessDescription && !!targetCustomer,
 *     step,            // current builder step
 *     onStart: handleGenerate,
 *   });
 *
 * Contract:
 *   * Only fires when `isOpen === true` AND `step === 'input'`
 *     AND `ready === true` AND foundation_complete === true.
 *   * Fires at MOST ONCE per open cycle.
 *   * Waits ~120ms after mount so useBusinessMemoryPrefill's async
 *     fetch can populate fields first.
 *   * Silent no-op when the foundation endpoint is unavailable —
 *     the customer simply sees the normal input form.
 */
import { useEffect, useRef } from 'react';
import useFoundationState from './useFoundationState';

export default function useFoundationAutoStart({ isOpen, ready, step, onStart }) {
  const { foundationComplete, loading } = useFoundationState(isOpen);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      startedRef.current = false;
      return undefined;
    }
    if (loading) return undefined;
    if (!foundationComplete) return undefined;
    if (step !== 'input') return undefined;
    if (!ready) return undefined;
    if (startedRef.current) return undefined;
    if (typeof onStart !== 'function') return undefined;

    // Small delay so useBusinessMemoryPrefill's async fetch has a
    // chance to hydrate any remaining empty fields before we fire.
    const t = setTimeout(() => {
      if (startedRef.current) return;
      startedRef.current = true;
      try {
        onStart();
      } catch (_) {
        startedRef.current = false;
      }
    }, 120);

    return () => clearTimeout(t);
  }, [isOpen, loading, foundationComplete, ready, step, onStart]);

  return { foundationComplete, loading };
}
